import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdir, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

/** Launcher for OpenCode CLI */
export class OpenCodeLauncher implements ProviderLauncher {
  readonly name = 'opencode'
  readonly displayName = 'OpenCode'
  readonly pricingHint = '💰 Nécessite une clé API (OpenAI/Anthropic/Google)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('opencode', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable('opencode-ai')
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('opencode', ['--version'])
      return stdout.trim()
    } catch {
      // See codex.ts — npx --version forces a network install on first run.
      return null
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('opencode', ['--version'])
      return 'opencode'
    } catch {
      return 'npx'
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    // OpenCode reads agent configs from ~/.config/opencode/agent/<name>.md.
    // Write our hidden system prompt there so the agent is "loaded" but
    // the candidate cannot see it (they don't have access to the home dir).
    const agentDir = join(process.env['HOME'] || '/tmp', '.config', 'opencode', 'agent')
    await mkdir(agentDir, { recursive: true })

    const agentConfig = `---
name: tonty-interview
description: Technical interview evaluation
mode: primary
permissions:
  - bash
  - read
  - edit
  - glob
  - grep
  - webfetch
  - websearch
---

${systemPrompt}
`
    const agentPath = join(agentDir, 'tonty-interview.md')
    await writeFile(agentPath, agentConfig)

    const args: string[] = []

    args.push(workdir)
    args.push('--agent', 'tonty-interview')

    if (config.model) {
      args.push('--model', config.model)
    }

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? ['opencode-ai', ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentPath, { force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentPath, { force: true }).catch(() => {})
      },
      recorder,
    }
  }
}
